import React, { useState, useEffect, useCallback, useRef } from 'react';

const API_URL = process.env.REACT_APP_API_URL || '/api';

// Inline SVG icons — Feather-style line art, matches App.js sidebar pattern (1.5 stroke, currentColor).
const Icon = ({ d, paths }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    {paths ? paths.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
  </svg>
);

const IconInbox    = () => <Icon paths={["M22 12h-6l-2 3h-4l-2-3H2","M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"]} />;
const IconStar     = () => <Icon d="M12 2 15.09 8.26 22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />;
const IconSend     = () => <Icon paths={["M22 2 11 13","M22 2l-7 20-4-9-9-4 20-7z"]} />;
const IconDraft    = () => <Icon paths={["M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z","M14 2v6h6","M16 13H8","M16 17H8","M10 9H8"]} />;
const IconTrash    = () => <Icon paths={["M3 6h18","M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2","M10 11v6","M14 11v6"]} />;
const IconPencil   = () => <Icon paths={["M12 20h9","M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"]} />;
const IconSearch   = () => <Icon paths={["M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16z","M21 21l-4.35-4.35"]} />;
const IconReply    = () => <Icon paths={["M9 17l-5-5 5-5","M4 12h11a5 5 0 0 1 5 5v3"]} />;
const IconReplyAll = () => <Icon paths={["M7 17l-5-5 5-5","M12 17l-5-5 5-5","M22 18v-1a4 4 0 0 0-4-4h-7"]} />;
const IconForward  = () => <Icon paths={["M15 17l5-5-5-5","M20 12H9a5 5 0 0 0-5 5v3"]} />;
const IconTag      = () => <Icon paths={["M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z","M7 7h.01"]} />;
const IconArchive  = () => <Icon paths={["M21 8v13H3V8","M1 3h22v5H1z","M10 12h4"]} />;
const IconStarOutline = () => <Icon d="M12 2 15.09 8.26 22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />;
const IconClose    = () => <Icon paths={["M18 6 6 18","M6 6l12 12"]} />;

// System folders that always appear in the sidebar, in display order.
// Lightweight WYSIWYG editor (contentEditable + execCommand) — no external deps.
// `value`/`onChange` carry HTML. Used for both the compose body and the signature.
function RichTextEditor({ value, onChange, placeholder, minHeight = 160, disabled }) {
  const ref = useRef(null);

  // Push external value into the editor only when it differs from what's shown,
  // so typing (which already syncs value via onInput) doesn't reset the caret.
  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== (value || '')) {
      ref.current.innerHTML = value || '';
    }
  }, [value]);

  const emit = () => { if (onChange && ref.current) onChange(ref.current.innerHTML); };
  const exec = (cmd, arg) => {
    try { document.execCommand('styleWithCSS', false, true); } catch (e) {}
    document.execCommand(cmd, false, arg);
    if (ref.current) ref.current.focus();
    emit();
  };

  const btn = { minWidth: 28, height: 28, padding: '0 6px', border: '1px solid var(--caam-border)', borderRadius: 5, background: '#fff', cursor: 'pointer', fontSize: 13, color: 'var(--caam-text)' };
  const sel = { height: 28, border: '1px solid var(--caam-border)', borderRadius: 5, background: '#fff', fontSize: 12, color: 'var(--caam-text)' };

  return (
    <div style={{ border: '1px solid var(--caam-border)', borderRadius: 8, overflow: 'hidden', background: '#fff' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, padding: 6, borderBottom: '1px solid var(--caam-border)', background: 'var(--caam-cream-deep)' }}>
        <button type="button" title="Bold" style={{ ...btn, fontWeight: 700 }} onMouseDown={(e) => { e.preventDefault(); exec('bold'); }}>B</button>
        <button type="button" title="Italic" style={{ ...btn, fontStyle: 'italic' }} onMouseDown={(e) => { e.preventDefault(); exec('italic'); }}>I</button>
        <button type="button" title="Underline" style={{ ...btn, textDecoration: 'underline' }} onMouseDown={(e) => { e.preventDefault(); exec('underline'); }}>U</button>
        <select title="Font" style={sel} defaultValue="" onChange={(e) => { if (e.target.value) exec('fontName', e.target.value); e.target.value = ''; }}>
          <option value="">Font</option>
          <option value="Arial">Arial</option>
          <option value="Georgia">Georgia</option>
          <option value="'Times New Roman'">Times</option>
          <option value="'Courier New'">Courier</option>
          <option value="Verdana">Verdana</option>
        </select>
        <select title="Size" style={sel} defaultValue="" onChange={(e) => { if (e.target.value) exec('fontSize', e.target.value); e.target.value = ''; }}>
          <option value="">Size</option>
          <option value="2">Small</option>
          <option value="3">Normal</option>
          <option value="5">Large</option>
          <option value="6">Huge</option>
        </select>
        <label title="Text color" style={{ ...btn, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
          A
          <input type="color" onChange={(e) => exec('foreColor', e.target.value)} style={{ width: 0, height: 0, opacity: 0, position: 'absolute' }} />
        </label>
        <button type="button" title="Bulleted list" style={btn} onMouseDown={(e) => { e.preventDefault(); exec('insertUnorderedList'); }}>•</button>
        <button type="button" title="Numbered list" style={btn} onMouseDown={(e) => { e.preventDefault(); exec('insertOrderedList'); }}>1.</button>
        <button type="button" title="Insert link" style={btn} onMouseDown={(e) => { e.preventDefault(); const u = window.prompt('Link URL:'); if (u) exec('createLink', u); }}>🔗</button>
        <button type="button" title="Clear formatting" style={btn} onMouseDown={(e) => { e.preventDefault(); exec('removeFormat'); }}>⌫</button>
      </div>
      <div
        ref={ref}
        className="rte-editor"
        contentEditable={!disabled}
        suppressContentEditableWarning
        onInput={emit}
        data-placeholder={placeholder || ''}
        style={{ minHeight, maxHeight: 320, overflowY: 'auto', padding: 12, fontSize: 13, lineHeight: 1.5, outline: 'none', color: 'var(--caam-text)' }}
      />
    </div>
  );
}

const SYSTEM_FOLDERS = [
  { id: 'INBOX',    label: 'Inbox',   Icon: IconInbox },
  { id: 'STARRED',  label: 'Starred', Icon: IconStar  },
  { id: 'SENT',     label: 'Sent',    Icon: IconSend  },
  { id: 'DRAFT',    label: 'Drafts',  Icon: IconDraft },
  { id: 'TRASH',    label: 'Trash',   Icon: IconTrash }
];

export default function Gmail({ onDiscuss }) {
  const [connected, setConnected]   = useState(null); // null = checking, true/false = known
  const [folder, setFolder]         = useState('INBOX');
  const [activeLabelId, setActiveLabelId] = useState(null); // when set, list filters by this label instead of folder
  const [selectedId, setSelectedId] = useState(null);
  const [labels, setLabels]         = useState([]);
  const [labelsLoading, setLabelsLoading] = useState(false);
  const [messages, setMessages]     = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError]   = useState(null);
  const [nextPageToken, setNextPageToken] = useState(null);  // pagination
  const [loadingMore, setLoadingMore] = useState(false);
  const [folderCounts, setFolderCounts] = useState({});      // { INBOX: n } unread per folder
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftSaved, setDraftSaved] = useState(false);
  const [labelMenuOpen, setLabelMenuOpen] = useState(false); // reading-pane "Label" dropdown
  const [discussOpen, setDiscussOpen] = useState(false);     // reading-pane "Discuss" person picker
  const [discussFilter, setDiscussFilter] = useState('');
  const [selectedIds, setSelectedIds] = useState(() => new Set()); // bulk-select message ids
  const [thread, setThread] = useState([]);                  // all messages in the open conversation
  const [expandedMsgs, setExpandedMsgs] = useState(() => new Set()); // expanded thread message ids
  const [query, setQuery]           = useState('');         // active search query (committed on Enter)
  const [queryDraft, setQueryDraft] = useState('');         // what's typed in the search box
  const [selectedMsg, setSelectedMsg] = useState(null);     // full message data when reading
  const [readLoading, setReadLoading] = useState(false);
  const [readError, setReadError]     = useState(null);
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeData, setComposeData] = useState({
    to: '', cc: '', bcc: '', subject: '', body: '',
    threadId: null, inReplyTo: null, references: null, draftId: null
  });
  const [showCcBcc, setShowCcBcc] = useState(false);
  const [attachFiles, setAttachFiles] = useState([]);  // [{ filename, mimeType, size, contentBase64 }]
  const [contacts, setContacts] = useState([]);        // staff directory for recipient autocomplete
  const [activeField, setActiveField] = useState(null); // which recipient field is focused
  const [signature, setSignature] = useState(() => { try { return localStorage.getItem('caam_gmail_signature') || ''; } catch (e) { return ''; } });
  const [showSigEditor, setShowSigEditor] = useState(false);
  const [composeQuoted, setComposeQuoted] = useState(''); // hidden quoted HTML for replies
  const [showQuoted, setShowQuoted]       = useState(false);
  const [sending, setSending]         = useState(false);
  const [sendError, setSendError]     = useState(null);
  const [sendSuccess, setSendSuccess] = useState(false);

  const authHeader = useCallback(() => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  }, []);

  // Initial connection check
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/google/status`, { headers: authHeader() });
        const data = await res.json();
        if (!cancelled) setConnected(!!data.connected);
      } catch (err) {
        if (!cancelled) setConnected(false);
      }
    })();
    return () => { cancelled = true; };
  }, [authHeader]);

  // Fetch user's labels once after connect (and refetch on reconnect)
  useEffect(() => {
    if (connected !== true) return;
    let cancelled = false;
    setLabelsLoading(true);
    (async () => {
      try {
        const res = await fetch(`${API_URL}/google/labels`, { headers: authHeader() });
        const data = await res.json();
        if (cancelled) return;
        if (!res.ok) {
          setLabels([]);
        } else {
          // Filter to user labels only, drop ones the user has marked hidden in Gmail settings
          const visible = (data.labels || []).filter(l =>
            l.type === 'user' && l.labelListVis !== 'labelHide'
          );
          // Sort alphabetically — Gmail sorts by name in the sidebar
          visible.sort((a, b) => a.name.localeCompare(b.name));
          setLabels(visible);
        }
      } catch (err) {
        if (!cancelled) setLabels([]);
      } finally {
        if (!cancelled) setLabelsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [connected, authHeader]);

  // Reusable fetcher — used by both the folder/query effect (shows loading) and the poller (silent refresh).
  // silent=true: don't clear messages, don't show loading spinner, don't reset selection.
  const fetchMessageList = useCallback(async ({ silent } = { silent: false }) => {
    if (connected !== true) return;
    if (!silent) {
      setListLoading(true);
      setListError(null);
      setSelectedId(null);
      setSelectedIds(new Set());
    }
    const params = new URLSearchParams({ maxResults: '50' });
    if (activeLabelId) {
      params.set('labelId', activeLabelId);
    } else {
      params.set('folder', folder);
    }
    if (query) params.set('q', query);
    // Drafts folder uses the drafts endpoint so each row carries its draftId (for editing).
    const isDrafts = folder === 'DRAFT' && !activeLabelId && !query;
    const url = isDrafts ? `${API_URL}/google/drafts` : `${API_URL}/google/messages?${params}`;
    try {
      const res = await fetch(url, { headers: authHeader() });
      const data = await res.json();
      if (!res.ok) {
        if (!silent) {
          setListError(data.error || 'Failed to load messages');
          setMessages([]);
        }
        return;
      }
      setMessages(data.messages || []);
      setNextPageToken(data.nextPageToken || null);
    } catch (err) {
      if (!silent) {
        setListError('Network error');
        setMessages([]);
      }
    } finally {
      if (!silent) setListLoading(false);
    }
  }, [connected, folder, activeLabelId, query, authHeader]);

  // Load the next page and append (pagination).
  const loadMore = async () => {
    if (!nextPageToken || loadingMore) return;
    setLoadingMore(true);
    const params = new URLSearchParams({ maxResults: '50', pageToken: nextPageToken });
    if (activeLabelId) params.set('labelId', activeLabelId); else params.set('folder', folder);
    if (query) params.set('q', query);
    try {
      const res = await fetch(`${API_URL}/google/messages?${params}`, { headers: authHeader() });
      const data = await res.json();
      if (res.ok) {
        setMessages(prev => [...prev, ...(data.messages || [])]);
        setNextPageToken(data.nextPageToken || null);
      }
    } catch (e) { /* keep current list on error */ }
    finally { setLoadingMore(false); }
  };

  // Poll the Inbox unread count for the sidebar badge.
  useEffect(() => {
    if (connected !== true) return;
    let stop = false;
    const fetchCounts = async () => {
      try {
        const res = await fetch(`${API_URL}/google/labels/counts?ids=INBOX`, { headers: authHeader() });
        const data = await res.json();
        if (!stop && res.ok) setFolderCounts(data.counts || {});
      } catch (e) { /* ignore */ }
    };
    fetchCounts();
    const id = setInterval(fetchCounts, 30000);
    return () => { stop = true; clearInterval(id); };
  }, [connected, authHeader]);

  // Load the staff directory once, for recipient autocomplete.
  useEffect(() => {
    let stop = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/staff`, { headers: authHeader() });
        const data = await res.json();
        if (!stop && res.ok) setContacts((Array.isArray(data) ? data : (data.staff || [])).filter(c => c.email));
      } catch (e) { /* ignore */ }
    })();
    return () => { stop = true; };
  }, [authHeader]);

  // Load the rest of the conversation when a message is opened (threading).
  useEffect(() => {
    const tid = selectedMsg && selectedMsg.threadId;
    if (!tid) { setThread([]); return; }
    let stop = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/google/threads/${tid}`, { headers: authHeader() });
        const data = await res.json();
        if (!stop && res.ok) { setThread(data.messages || []); setExpandedMsgs(new Set()); }
      } catch (e) { if (!stop) setThread([]); }
    })();
    return () => { stop = true; };
  }, [selectedMsg && selectedMsg.threadId, authHeader]);

  // Initial + folder/query-change fetch (with loading state)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      await fetchMessageList({ silent: false });
      // cancelled is informational; fetchMessageList doesn't expose abort, but it's fine —
      // setState calls after unmount are harmless warnings, not bugs
      if (cancelled) return;
    })();
    return () => { cancelled = true; };
  }, [fetchMessageList]);

  // Auto-refresh every 30s when in Inbox or viewing a label. Silent — preserves selection.
  // Pauses when the tab is hidden, or when the compose modal is open.
  useEffect(() => {
    if (connected !== true) return;
    // Auto-refresh in Inbox or when viewing a label; skip for static folders (Sent/Drafts/Trash)
    if (folder !== 'INBOX' && !activeLabelId) return;
    if (composeOpen) return;
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;

    const tick = () => {
      // Re-check visibility each tick — interval keeps firing on hidden tabs in some browsers
      if (document.visibilityState !== 'hidden') {
        fetchMessageList({ silent: true });
      }
    };
    const id = setInterval(tick, 30000);
    return () => clearInterval(id);
  }, [connected, folder, activeLabelId, composeOpen, fetchMessageList]);

  // When the tab becomes visible again, do an immediate refresh (so users coming back to the tab see fresh mail)
  useEffect(() => {
    if (connected !== true) return;
    const onVisible = () => {
      if (document.visibilityState === 'visible'
          && (folder === 'INBOX' || activeLabelId)
          && !composeOpen) {
        fetchMessageList({ silent: true });
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [connected, folder, activeLabelId, composeOpen, fetchMessageList]);

  // Connect flow — open Google consent in a popup
  const handleConnect = async () => {
    try {
      const res = await fetch(`${API_URL}/google/auth`, { headers: authHeader() });
      const data = await res.json();
      if (!data.url) return;

      const popup = window.open(data.url, 'gmail_connect', 'width=520,height=640');

      // Listen for the postMessage from the OAuth callback page
      const onMessage = (event) => {
        if (event.data && event.data.type === 'GMAIL_CONNECTED') {
          setConnected(true);
          window.removeEventListener('message', onMessage);
          if (popup && !popup.closed) popup.close();
        }
      };
      window.addEventListener('message', onMessage);
    } catch (err) {
      console.error('Gmail connect error:', err);
    }
  };

  // Fetch full message when selection changes
  useEffect(() => {
    if (!selectedId) {
      setSelectedMsg(null);
      return;
    }
    let cancelled = false;
    setReadLoading(true);
    setReadError(null);
    (async () => {
      try {
        const res = await fetch(`${API_URL}/google/messages/${selectedId}`, { headers: authHeader() });
        const data = await res.json();
        if (cancelled) return;
        if (!res.ok) {
          setReadError(data.error || 'Failed to load message');
          setSelectedMsg(null);
        } else {
          setSelectedMsg(data);
        }
      } catch (err) {
        if (!cancelled) {
          setReadError('Network error');
          setSelectedMsg(null);
        }
      } finally {
        if (!cancelled) setReadLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedId, authHeader]);

  // Archive — remove from list, deselect
  const handleArchive = async () => {
    if (!selectedId) return;
    const id = selectedId;
    try {
      await fetch(`${API_URL}/google/messages/${id}/archive`, {
        method: 'POST',
        headers: authHeader()
      });
      setMessages(prev => prev.filter(m => m.id !== id));
      setSelectedId(null);
    } catch (err) {
      console.error('Archive failed:', err);
    }
  };

  // Delete — same pattern but DELETE
  const handleDelete = async () => {
    if (!selectedId) return;
    const id = selectedId;
    try {
      await fetch(`${API_URL}/google/messages/${id}`, {
        method: 'DELETE',
        headers: authHeader()
      });
      setMessages(prev => prev.filter(m => m.id !== id));
      setSelectedId(null);
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  // Star — toggle on the server, update the local row optimistically
  const handleStar = async () => {
    if (!selectedId) return;
    const id = selectedId;
    const isStarred = selectedMsg && (selectedMsg.labelIds || []).includes('STARRED');
    try {
      await fetch(`${API_URL}/google/messages/${id}/star`, {
        method: 'POST',
        headers: { ...authHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ star: !isStarred })
      });
      // Optimistic local toggle
      if (selectedMsg) {
        const newLabels = isStarred
          ? (selectedMsg.labelIds || []).filter(l => l !== 'STARRED')
          : [...(selectedMsg.labelIds || []), 'STARRED'];
        setSelectedMsg({ ...selectedMsg, labelIds: newLabels });
      }
    } catch (err) {
      console.error('Star failed:', err);
    }
  };

  // Open compose modal blank (Compose button)
  const openCompose = () => {
    setComposeData({ to: '', cc: '', bcc: '', subject: '', body: signature ? '<br><br>' + signature : '', threadId: null, inReplyTo: null, references: null, draftId: null });
    setComposeQuoted('');
    setShowQuoted(false);
    setShowCcBcc(false);
    setSendError(null);
    setSendSuccess(false);
    setComposeOpen(true);
  };

  // Reply — open compose with clean blank body; quoted original stored separately and hidden by default
  const handleReply = () => {
    if (!selectedMsg) return;
    const origSubject = selectedMsg.subject || '';
    const prefixedSubject = /^re:\s*/i.test(origSubject) ? origSubject : `Re: ${origSubject}`;
    const fromEmail = parseFromEmail(selectedMsg.from);
    const fromName  = parseFromName(selectedMsg.from);
    const dateLine  = selectedMsg.date ? formatReadDate(selectedMsg.date) : '';

    const quotedHeader = `<p>On ${escapeHtml(dateLine)}, ${escapeHtml(fromName)} &lt;${escapeHtml(fromEmail)}&gt; wrote:</p>`;
    const quotedBody = `<blockquote style="margin:0 0 0 8px; padding-left:12px; border-left:2px solid var(--caam-text-light); color:var(--caam-text-muted);">${selectedMsg.body || ''}</blockquote>`;

    setComposeData({
      to:         fromEmail,
      cc:         '',
      bcc:        '',
      subject:    prefixedSubject,
      body:       '', // user starts with a blank composition area
      threadId:   selectedMsg.threadId || null,
      inReplyTo:  selectedMsg.messageId || null,
      references: selectedMsg.messageId || null
    });
    setComposeQuoted(quotedHeader + quotedBody);
    setShowQuoted(false);
    setShowCcBcc(false);
    setSendError(null);
    setSendSuccess(false);
    setComposeOpen(true);
  };

  // Reply All — to = original sender + all other To/Cc recipients, minus yourself.
  const handleReplyAll = () => {
    if (!selectedMsg) return;
    const origSubject = selectedMsg.subject || '';
    const prefixedSubject = /^re:\s*/i.test(origSubject) ? origSubject : `Re: ${origSubject}`;
    const fromEmail = parseFromEmail(selectedMsg.from);
    const fromName  = parseFromName(selectedMsg.from);
    const dateLine  = selectedMsg.date ? formatReadDate(selectedMsg.date) : '';

    let myEmail = '';
    try { myEmail = (JSON.parse(localStorage.getItem('user') || '{}').email || '').toLowerCase(); } catch (e) {}
    const extract = (str) => (str || '').split(',').map(s => parseFromEmail(s.trim())).filter(Boolean);
    // To = the original sender; Cc = everyone else on To/Cc — both minus yourself, deduped.
    const seen = new Set();
    if (myEmail) seen.add(myEmail);
    const dedupe = (list) => list.filter(e => {
      const k = (e || '').toLowerCase();
      if (!k || seen.has(k)) return false;
      seen.add(k);
      return true;
    });
    const toList = dedupe([fromEmail]);
    const ccList = dedupe([...extract(selectedMsg.to), ...extract(selectedMsg.cc)]);

    const quotedHeader = `<p>On ${escapeHtml(dateLine)}, ${escapeHtml(fromName)} &lt;${escapeHtml(fromEmail)}&gt; wrote:</p>`;
    const quotedBody = `<blockquote style="margin:0 0 0 8px; padding-left:12px; border-left:2px solid var(--caam-text-light); color:var(--caam-text-muted);">${selectedMsg.body || ''}</blockquote>`;

    setComposeData({
      to:         toList.join(', '),
      cc:         ccList.join(', '),
      bcc:        '',
      subject:    prefixedSubject,
      body:       '',
      threadId:   selectedMsg.threadId || null,
      inReplyTo:  selectedMsg.messageId || null,
      references: selectedMsg.messageId || null
    });
    setComposeQuoted(quotedHeader + quotedBody);
    setShowQuoted(false);
    setShowCcBcc(ccList.length > 0);
    setSendError(null);
    setSendSuccess(false);
    setComposeOpen(true);
  };

  // Forward — new message with the original quoted, recipient left blank for the user.
  const handleForward = () => {
    if (!selectedMsg) return;
    const origSubject = selectedMsg.subject || '';
    const prefixedSubject = /^fwd?:\s*/i.test(origSubject) ? origSubject : `Fwd: ${origSubject}`;
    const fromEmail = parseFromEmail(selectedMsg.from);
    const fromName  = parseFromName(selectedMsg.from);
    const dateLine  = selectedMsg.date ? formatReadDate(selectedMsg.date) : '';

    const fwdHeader = `<p>---------- Forwarded message ----------<br>`
      + `From: ${escapeHtml(fromName)} &lt;${escapeHtml(fromEmail)}&gt;<br>`
      + `Date: ${escapeHtml(dateLine)}<br>`
      + `Subject: ${escapeHtml(origSubject)}<br>`
      + `To: ${escapeHtml(selectedMsg.to || '')}</p>`;
    const fwdBody = `<div>${selectedMsg.body || ''}</div>`;

    setComposeData({
      to:         '',
      cc:         '',
      bcc:        '',
      subject:    prefixedSubject,
      body:       '',
      threadId:   null,        // a forward starts a new conversation, not a reply
      inReplyTo:  null,
      references: null
    });
    setComposeQuoted(fwdHeader + fwdBody);
    setShowQuoted(true);       // forwarded content is shown by default
    setShowCcBcc(false);
    setSendError(null);
    setSendSuccess(false);
    setComposeOpen(true);
  };

  // Download an attachment from the open message (authed fetch -> blob -> save).
  const downloadAttachment = async (att) => {
    try {
      const qs = new URLSearchParams({ filename: att.filename || 'attachment', mimeType: att.mimeType || 'application/octet-stream' });
      const res = await fetch(`${API_URL}/google/messages/${selectedMsg.id}/attachments/${att.attachmentId}?${qs}`, { headers: authHeader() });
      if (!res.ok) return;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = att.filename || 'attachment';
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) { /* ignore */ }
  };

  // Mark the open message read/unread, then silently refresh the list.
  const toggleUnread = async () => {
    if (!selectedMsg) return;
    const isUnread = (selectedMsg.labelIds || []).includes('UNREAD');
    try {
      await fetch(`${API_URL}/google/messages/${selectedMsg.id}/modify`, {
        method: 'POST',
        headers: { ...authHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify(isUnread ? { removeLabelIds: ['UNREAD'] } : { addLabelIds: ['UNREAD'] })
      });
      const newLabels = isUnread
        ? (selectedMsg.labelIds || []).filter(l => l !== 'UNREAD')
        : [...(selectedMsg.labelIds || []), 'UNREAD'];
      setSelectedMsg({ ...selectedMsg, labelIds: newLabels });
      fetchMessageList({ silent: true });
    } catch (e) { /* ignore */ }
  };

  // Add/remove a user label on the open message.
  const toggleLabel = async (labelId) => {
    if (!selectedMsg) return;
    const has = (selectedMsg.labelIds || []).includes(labelId);
    try {
      await fetch(`${API_URL}/google/messages/${selectedMsg.id}/modify`, {
        method: 'POST', headers: { ...authHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify(has ? { removeLabelIds: [labelId] } : { addLabelIds: [labelId] })
      });
      const newLabels = has
        ? (selectedMsg.labelIds || []).filter(l => l !== labelId)
        : [...(selectedMsg.labelIds || []), labelId];
      setSelectedMsg({ ...selectedMsg, labelIds: newLabels });
    } catch (e) { /* ignore */ }
  };

  // Discuss the open email in chat: open a chat with the chosen colleague and attach an email
  // snapshot so they can open it in a reading pane even if they weren't on the chain.
  const startDiscussion = (contact) => {
    if (!selectedMsg || !onDiscuss) return;
    const who = `${contact.first_name || ''} ${contact.last_name || ''}`.trim();
    onDiscuss(who, {
      subject: selectedMsg.subject || '(no subject)',
      from: selectedMsg.from || '',
      date: selectedMsg.date || '',
      body: selectedMsg.body || ''
    });
    setDiscussOpen(false);
    setDiscussFilter('');
  };

  // ---- Bulk select ----
  const toggleRowSelect = (id) => setSelectedIds(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const clearSelection = () => setSelectedIds(new Set());
  const bulkAction = async (type) => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    await Promise.all(ids.map(id => {
      if (type === 'archive') return fetch(`${API_URL}/google/messages/${id}/archive`, { method: 'POST', headers: authHeader() });
      if (type === 'delete')  return fetch(`${API_URL}/google/messages/${id}`, { method: 'DELETE', headers: authHeader() });
      if (type === 'read')    return fetch(`${API_URL}/google/messages/${id}/modify`, { method: 'POST', headers: { ...authHeader(), 'Content-Type': 'application/json' }, body: JSON.stringify({ removeLabelIds: ['UNREAD'] }) });
      if (type === 'unread')  return fetch(`${API_URL}/google/messages/${id}/modify`, { method: 'POST', headers: { ...authHeader(), 'Content-Type': 'application/json' }, body: JSON.stringify({ addLabelIds: ['UNREAD'] }) });
      return Promise.resolve();
    }).map(p => (p && p.catch ? p.catch(() => {}) : p)));
    clearSelection();
    fetchMessageList({ silent: false });
  };

  // Compose attachments: read picked files as base64 for the send payload.
  const onAttachFiles = (e) => {
    const files = Array.from(e.target.files || []);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = String(reader.result).split(',')[1] || '';
        setAttachFiles(prev => [...prev, { filename: file.name, mimeType: file.type || 'application/octet-stream', size: file.size, contentBase64: base64 }]);
      };
      reader.readAsDataURL(file);
    });
    e.target.value = '';
  };
  const removeAttach = (i) => setAttachFiles(prev => prev.filter((_, idx) => idx !== i));

  // Clear picked attachments whenever the compose modal closes (prevents stale carry-over).
  useEffect(() => { if (!composeOpen) setAttachFiles([]); }, [composeOpen]);

  // ---- Recipient autocomplete (To/Cc/Bcc) ----
  const lastToken = (v) => { const parts = String(v || '').split(','); return parts[parts.length - 1].trim(); };
  const suggestionsFor = (field) => {
    if (activeField !== field) return [];
    const tok = lastToken(composeData[field]).toLowerCase();
    if (tok.length < 2) return [];
    return contacts.filter(c => {
      const name = `${c.first_name || ''} ${c.last_name || ''}`.toLowerCase();
      return (c.email || '').toLowerCase().includes(tok) || name.includes(tok);
    }).slice(0, 6);
  };
  const applyContact = (field, email) => {
    setComposeData(cd => {
      const parts = String(cd[field] || '').split(',');
      parts[parts.length - 1] = ' ' + email;
      return { ...cd, [field]: parts.join(',').replace(/^\s+/, '') + ', ' };
    });
    setActiveField(null);
  };
  const recipientField = (field, labelText, placeholder) => (
    <label style={{ ...styles.modalLabel, position: 'relative' }}>
      <span style={styles.modalLabelText}>{labelText}</span>
      <input
        type="text"
        value={composeData[field]}
        onChange={(e) => setComposeData({ ...composeData, [field]: e.target.value })}
        onFocus={() => setActiveField(field)}
        onBlur={() => setTimeout(() => setActiveField(f => (f === field ? null : f)), 150)}
        placeholder={placeholder}
        style={styles.modalInput}
        autoFocus={field === 'to' && !composeData.to}
        disabled={sending}
      />
      {field === 'to' && !showCcBcc && (
        <button type="button" onClick={() => setShowCcBcc(true)}
          style={{ background: 'none', border: 'none', color: 'var(--caam-accent)', fontSize: 12, cursor: 'pointer', padding: '0 4px', flexShrink: 0 }}>
          Cc/Bcc
        </button>
      )}
      {suggestionsFor(field).length > 0 && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 30, background: '#fff', border: '1px solid var(--caam-border)', borderRadius: 8, boxShadow: '0 6px 24px rgba(0,0,0,0.12)', maxHeight: 200, overflowY: 'auto', marginTop: 2 }}>
          {suggestionsFor(field).map(c => (
            <div key={c.id || c.email} onMouseDown={(e) => { e.preventDefault(); applyContact(field, c.email); }}
              style={{ padding: '8px 12px', fontSize: 13, cursor: 'pointer' }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'var(--caam-cream-deep)'}
              onMouseLeave={(e) => e.currentTarget.style.background = '#fff'}>
              <strong>{c.first_name} {c.last_name}</strong> <span style={{ color: 'var(--caam-text-light)' }}>{c.email}</span>
            </div>
          ))}
        </div>
      )}
    </label>
  );

  // Body is already HTML from the rich-text editor; just append any quoted/forwarded original.
  const buildBodyHtml = () => {
    return (composeData.body || '') + (composeQuoted ? '<br><br>' + composeQuoted : '');
  };

  // Open an existing draft into the compose modal for editing.
  const openDraft = async (draftId) => {
    try {
      const res = await fetch(`${API_URL}/google/drafts/${draftId}`, { headers: authHeader() });
      const d = await res.json();
      if (!res.ok) return;
      setComposeData({
        to: d.to || '', cc: d.cc || '', bcc: d.bcc || '', subject: d.subject || '',
        body: d.body || '',
        threadId: null, inReplyTo: null, references: null, draftId
      });
      setComposeQuoted('');
      setShowQuoted(false);
      setShowCcBcc(!!(d.cc || d.bcc));
      setSendError(null); setSendSuccess(false); setDraftSaved(false);
      setComposeOpen(true);
    } catch (e) { /* ignore */ }
  };

  // Save the current compose as a Gmail draft (create or update).
  const saveDraft = async () => {
    if (savingDraft) return;
    setSavingDraft(true); setDraftSaved(false);
    try {
      const payload = {
        to: composeData.to, cc: composeData.cc, bcc: composeData.bcc,
        subject: composeData.subject, body: buildBodyHtml(), attachments: attachFiles
      };
      const url = composeData.draftId ? `${API_URL}/google/drafts/${composeData.draftId}` : `${API_URL}/google/drafts`;
      const res = await fetch(url, {
        method: composeData.draftId ? 'PUT' : 'POST',
        headers: { ...authHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) { setComposeData(cd => ({ ...cd, draftId: data.draftId })); setDraftSaved(true); }
    } catch (e) { /* ignore */ }
    finally { setSavingDraft(false); }
  };

  // Submit compose — sends a draft (if editing one) or a fresh message, then closes.
  const submitCompose = async (e) => {
    e.preventDefault();
    if (sending) return;
    if (!composeData.to.trim() || !composeData.subject.trim()) {
      setSendError('To and Subject are required.');
      return;
    }
    setSending(true);
    setSendError(null);
    try {
      const finalBody = buildBodyHtml();
      let res;
      if (composeData.draftId) {
        // Persist latest edits to the draft, then send it (removes it from Drafts).
        await fetch(`${API_URL}/google/drafts/${composeData.draftId}`, {
          method: 'PUT', headers: { ...authHeader(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ to: composeData.to, cc: composeData.cc, bcc: composeData.bcc, subject: composeData.subject, body: finalBody, attachments: attachFiles })
        });
        res = await fetch(`${API_URL}/google/drafts/${composeData.draftId}/send`, { method: 'POST', headers: authHeader() });
      } else {
        const payload = { ...composeData, body: finalBody, attachments: attachFiles };
        res = await fetch(`${API_URL}/google/send`, {
          method: 'POST', headers: { ...authHeader(), 'Content-Type': 'application/json' }, body: JSON.stringify(payload)
        });
      }
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setSendError(data.error || 'Failed to send.');
        setSending(false);
        return;
      }
      setSendSuccess(true);
      setSending(false);
      setTimeout(() => {
        setComposeOpen(false);
        setSendSuccess(false);
        if (folder === 'SENT' || folder === 'DRAFT') fetchMessageList({ silent: false });
      }, 600);
    } catch (err) {
      setSendError('Network error.');
      setSending(false);
    }
  };

  // Click a message — select it, mark read. Draft rows open in compose instead.
  const handleSelectMessage = async (msg) => {
    if (msg.draftId) { openDraft(msg.draftId); return; }
    setSelectedId(msg.id);
    setLabelMenuOpen(false);
    setDiscussOpen(false);
    if (msg.unread) {
      // Optimistic: drop UNREAD locally so the list re-renders immediately
      setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, unread: false } : m));
      try {
        await fetch(`${API_URL}/google/messages/${msg.id}/read`, {
          method: 'POST',
          headers: authHeader()
        });
      } catch (err) {
        // Non-fatal — list will resync on next fetch
        console.error('Failed to mark read:', err);
      }
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setQuery(queryDraft.trim());
  };

  // ===== Render states =====

  if (connected === null) {
    return (
      <div style={styles.centered}>
        <div style={styles.muted}>Checking Gmail connection…</div>
      </div>
    );
  }

  if (connected === false) {
    return (
      <div style={styles.centered}>
        <h2 style={{ margin: '0 0 8px 0', fontSize: 18, fontWeight: 500 }}>Connect Gmail</h2>
        <p style={{ ...styles.muted, marginBottom: 16, maxWidth: 360, textAlign: 'center' }}>
          Sign in with your CAAM Google account to read and send mail inside MiiMuseum.
        </p>
        <button style={styles.primaryBtn} onClick={handleConnect}>
          Connect Gmail
        </button>
      </div>
    );
  }

  // Connected — render the three-pane shell with placeholders
  return (
    <div style={styles.shell}>
      {/* Left: folders + labels */}
      <aside style={styles.folderPane}>
        <button style={styles.composeBtn} onClick={openCompose}>
          <span style={{ display: 'inline-flex', alignItems: 'center', marginRight: 8 }}><IconPencil /></span>
          Compose
        </button>
        <nav style={styles.folderList}>
          {SYSTEM_FOLDERS.map(f => (
            <button
              key={f.id}
              onClick={() => { setFolder(f.id); setActiveLabelId(null); setSelectedId(null); }}
              style={{
                ...styles.folderItem,
                ...(folder === f.id && !activeLabelId ? styles.folderItemActive : {})
              }}
            >
              <span style={{ marginRight: 10, display: 'inline-flex', alignItems: 'center' }}><f.Icon /></span>
              <span>{f.label}</span>
              {f.id === 'INBOX' && folderCounts.INBOX > 0 && (
                <span style={{ marginLeft: 'auto', minWidth: 18, height: 18, padding: '0 5px', borderRadius: 9, background: 'var(--caam-accent)', color: '#fff', fontSize: 10, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                  {folderCounts.INBOX > 99 ? '99+' : folderCounts.INBOX}
                </span>
              )}
            </button>
          ))}
        </nav>
        {labels.length > 0 && (
          <>
            <div style={styles.labelsHeading}>Labels</div>
            <nav style={styles.folderList}>
              {labels.map(l => {
                const isActive = activeLabelId === l.id;
                const dotColor = (l.color && l.color.backgroundColor) || 'var(--caam-text-light)';
                return (
                  <button
                    key={l.id}
                    onClick={() => { setActiveLabelId(l.id); setSelectedId(null); }}
                    style={{
                      ...styles.folderItem,
                      ...(isActive ? styles.folderItemActive : {})
                    }}
                    title={l.name}
                  >
                    <span style={{ ...styles.labelDot, background: dotColor }} />
                    <span style={styles.labelName}>{l.name}</span>
                  </button>
                );
              })}
            </nav>
          </>
        )}
        {labelsLoading && labels.length === 0 && (
          <div style={{ ...styles.muted, padding: '12px' }}>Loading labels…</div>
        )}
      </aside>

      {/* Middle: message list */}
      <section style={styles.listPane}>
        <form onSubmit={handleSearchSubmit} style={styles.searchBar}>
          <span style={styles.searchIcon}><IconSearch /></span>
          <input
            type="text"
            placeholder="Search mail"
            value={queryDraft}
            onChange={(e) => setQueryDraft(e.target.value)}
            style={styles.searchInput}
          />
          {query && (
            <button
              type="button"
              onClick={() => { setQueryDraft(''); setQuery(''); }}
              style={styles.searchClear}
              aria-label="Clear search"
            >
              ×
            </button>
          )}
        </form>
        {selectedIds.size > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderBottom: '1px solid var(--caam-border)', background: 'var(--caam-cream-deep)', fontSize: 12 }}>
            <span style={{ fontWeight: 600 }}>{selectedIds.size} selected</span>
            <button onClick={() => bulkAction('read')} style={styles.bulkBtn}>Read</button>
            <button onClick={() => bulkAction('unread')} style={styles.bulkBtn}>Unread</button>
            <button onClick={() => bulkAction('archive')} style={styles.bulkBtn}>Archive</button>
            <button onClick={() => bulkAction('delete')} style={styles.bulkBtn}>Delete</button>
            <button onClick={clearSelection} style={{ ...styles.bulkBtn, marginLeft: 'auto' }}>Clear</button>
          </div>
        )}
        <div style={styles.listScroller}>
          {listLoading && (
            <div style={{ ...styles.muted, padding: 16 }}>Loading…</div>
          )}
          {!listLoading && listError && (
            <div style={{ padding: 16, color: '#9B3A1F', fontSize: 13 }}>{listError}</div>
          )}
          {!listLoading && !listError && messages.length === 0 && (
            <div style={{ ...styles.muted, padding: 16 }}>
              {query ? 'No messages match your search.' : 'No messages.'}
            </div>
          )}
          {!listLoading && !listError && messages.map(msg => {
            const isSelected = msg.id === selectedId;
            const isChecked = selectedIds.has(msg.id);
            return (
              <div key={msg.id} style={{ display: 'flex', alignItems: 'stretch', background: isChecked ? 'var(--caam-accent-tint)' : 'transparent' }}>
                {!msg.draftId && (
                  <input type="checkbox" checked={isChecked}
                    onChange={() => toggleRowSelect(msg.id)}
                    onClick={(e) => e.stopPropagation()}
                    style={{ margin: '0 12px 0 10px', alignSelf: 'center', flexShrink: 0 }} />
                )}
                <button
                  onClick={() => handleSelectMessage(msg)}
                  style={{
                    ...styles.msgRow,
                    flex: 1,
                    ...(isSelected ? styles.msgRowSelected : {}),
                    ...(msg.unread ? styles.msgRowUnread : {})
                  }}
                >
                <div style={styles.msgRowTop}>
                  <span style={styles.msgRowFrom}>{parseFromName(msg.from)}</span>
                  <span style={styles.msgRowDate}>{formatMsgDate(msg.date)}</span>
                </div>
                <div style={styles.msgRowSubject}>{msg.subject}</div>
                <div style={styles.msgRowSnippet}
                     dangerouslySetInnerHTML={{ __html: msg.snippet || '' }} />
                </button>
              </div>
            );
          })}
          {!listLoading && !listError && nextPageToken && (
            <button onClick={loadMore} disabled={loadingMore}
              style={{ display: 'block', width: 'calc(100% - 16px)', margin: '8px', padding: '8px', background: 'var(--caam-cream-deep)', border: '1px solid var(--caam-border)', borderRadius: 6, fontSize: 13, color: 'var(--caam-text-muted)', cursor: loadingMore ? 'default' : 'pointer' }}>
              {loadingMore ? 'Loading…' : 'Load more'}
            </button>
          )}
        </div>
      </section>

      {composeOpen && (
        <div style={styles.modalOverlay} onClick={() => !sending && setComposeOpen(false)}>
          <div style={styles.modalCard} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <span style={styles.modalTitle}>
                {composeData.threadId ? 'Reply' : 'New message'}
              </span>
              <button
                style={styles.modalClose}
                onClick={() => !sending && setComposeOpen(false)}
                aria-label="Close"
                disabled={sending}
              >
                <IconClose />
              </button>
            </div>
            <form onSubmit={submitCompose} style={styles.modalForm}>
              {recipientField('to', 'To', 'recipient@example.com')}
              {showCcBcc && recipientField('cc', 'Cc', 'cc@example.com')}
              {showCcBcc && recipientField('bcc', 'Bcc', 'bcc@example.com')}
              <label style={styles.modalLabel}>
                <span style={styles.modalLabelText}>Subject</span>
                <input
                  type="text"
                  value={composeData.subject}
                  onChange={(e) => setComposeData({ ...composeData, subject: e.target.value })}
                  style={styles.modalInput}
                  disabled={sending}
                />
              </label>
              <RichTextEditor
                value={composeData.body}
                onChange={(html) => setComposeData(cd => ({ ...cd, body: html }))}
                placeholder="Write your message…"
                minHeight={180}
                disabled={sending}
              />
              {composeQuoted && (
                <div style={styles.quotedToggleWrap}>
                  <button
                    type="button"
                    style={styles.quotedToggle}
                    onClick={() => setShowQuoted(v => !v)}
                    aria-expanded={showQuoted}
                    title={showQuoted ? 'Hide quoted text' : 'Show quoted text'}
                  >
                    {showQuoted ? '▾  Hide quoted text' : '⋯  Show quoted text'}
                  </button>
                  {showQuoted && (
                    <div
                      style={styles.quotedPreview}
                      dangerouslySetInnerHTML={{ __html: composeQuoted }}
                    />
                  )}
                </div>
              )}
              {attachFiles.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                  {attachFiles.map((f, i) => (
                    <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'var(--caam-cream-deep)', border: '1px solid var(--caam-border)', borderRadius: 6, padding: '4px 8px', fontSize: 12 }}>
                      {f.filename} <span style={{ color: 'var(--caam-text-light)' }}>({Math.max(1, Math.ceil((f.size || 0) / 1024))} KB)</span>
                      <button type="button" onClick={() => removeAttach(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--caam-text-muted)', padding: 0, fontSize: 14, lineHeight: 1 }}>×</button>
                    </span>
                  ))}
                </div>
              )}
              {sendError && (
                <div style={styles.modalError}>{sendError}</div>
              )}
              {sendSuccess && (
                <div style={styles.modalSuccess}>Sent.</div>
              )}
              {showSigEditor && (
                <div style={{ marginTop: 8, padding: 10, border: '1px solid var(--caam-border)', borderRadius: 8, background: 'var(--caam-cream-deep)' }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--caam-text-muted)', marginBottom: 6 }}>Your signature (added to new messages)</div>
                  <RichTextEditor value={signature}
                    onChange={(html) => { setSignature(html); try { localStorage.setItem('caam_gmail_signature', html); } catch (err) {} }}
                    placeholder="Your signature" minHeight={90} />
                  <div style={{ fontSize: 11, color: 'var(--caam-text-light)', marginTop: 4 }}>Saved automatically · applies to new messages.</div>
                </div>
              )}
              <div style={styles.modalFooter}>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 10px', fontSize: 13, color: 'var(--caam-text-muted)', cursor: sending ? 'default' : 'pointer' }} title="Attach files">
                  <input type="file" multiple onChange={onAttachFiles} style={{ display: 'none' }} disabled={sending} />
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>
                  Attach
                </label>
                <button type="button" onClick={saveDraft} disabled={savingDraft || sending}
                  style={{ padding: '8px 10px', fontSize: 13, color: 'var(--caam-text-muted)', background: 'none', border: 'none', cursor: (savingDraft || sending) ? 'default' : 'pointer' }}>
                  {savingDraft ? 'Saving…' : draftSaved ? 'Saved ✓' : 'Save draft'}
                </button>
                <button type="button" onClick={() => setShowSigEditor(v => !v)}
                  style={{ marginRight: 'auto', padding: '8px 10px', fontSize: 13, color: 'var(--caam-text-muted)', background: 'none', border: 'none', cursor: 'pointer' }}>
                  Signature
                </button>
                <button
                  type="button"
                  style={styles.modalCancel}
                  onClick={() => setComposeOpen(false)}
                  disabled={sending}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={styles.modalSend}
                  disabled={sending}
                >
                  {sending ? 'Sending…' : 'Send'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Right: reading pane */}
      <section style={styles.readPane}>
        {!selectedId && (
          <div style={{ ...styles.muted, padding: 40, textAlign: 'center' }}>
            Select a message to read.
          </div>
        )}
        {selectedId && readLoading && (
          <div style={{ ...styles.muted, padding: 24 }}>Loading…</div>
        )}
        {selectedId && readError && (
          <div style={{ padding: 24, color: '#9B3A1F', fontSize: 13 }}>{readError}</div>
        )}
        {selectedId && !readLoading && !readError && selectedMsg && (
          <>
            <header style={styles.readHeader}>
              <div style={styles.readActionsRow}>
                <button style={styles.readActionBtn} onClick={handleReply} title="Reply">
                  <IconReply />
                  <span style={{ marginLeft: 6 }}>Reply</span>
                </button>
                <button style={styles.readActionBtn} onClick={handleReplyAll} title="Reply to all">
                  <IconReplyAll />
                  <span style={{ marginLeft: 6 }}>Reply All</span>
                </button>
                <button style={styles.readActionBtn} onClick={handleForward} title="Forward">
                  <IconForward />
                  <span style={{ marginLeft: 6 }}>Forward</span>
                </button>
                <div style={{ position: 'relative' }}>
                  <button style={styles.readActionBtn} onClick={() => setLabelMenuOpen(o => !o)} title="Label">
                    <IconTag />
                    <span style={{ marginLeft: 6 }}>Label</span>
                  </button>
                  {labelMenuOpen && (
                    <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 20, marginTop: 4, background: '#fff', border: '1px solid var(--caam-border)', borderRadius: 8, boxShadow: '0 6px 24px rgba(0,0,0,0.12)', minWidth: 200, maxHeight: 260, overflowY: 'auto', padding: 4 }}>
                      {labels.filter(l => l.type === 'user').length === 0 && (
                        <div style={{ padding: 10, fontSize: 12, color: 'var(--caam-text-light)' }}>No labels</div>
                      )}
                      {labels.filter(l => l.type === 'user').map(l => (
                        <label key={l.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', fontSize: 13, cursor: 'pointer' }}>
                          <input type="checkbox" checked={(selectedMsg.labelIds || []).includes(l.id)} onChange={() => toggleLabel(l.id)} />
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.name}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
                {onDiscuss && (
                  <div style={{ position: 'relative' }}>
                    <button style={styles.readActionBtn} onClick={() => setDiscussOpen(o => !o)} title="Discuss this email in chat">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                      <span style={{ marginLeft: 6 }}>Discuss</span>
                    </button>
                    {discussOpen && (
                      <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 20, marginTop: 4, background: '#fff', border: '1px solid var(--caam-border)', borderRadius: 8, boxShadow: '0 6px 24px rgba(0,0,0,0.12)', width: 260, padding: 6 }}>
                        <input autoFocus value={discussFilter} onChange={(e) => setDiscussFilter(e.target.value)} placeholder="Chat with…"
                          style={{ width: '100%', padding: '7px 9px', border: '1px solid var(--caam-border)', borderRadius: 6, fontSize: 12, outline: 'none', boxSizing: 'border-box', marginBottom: 6 }} />
                        <div style={{ maxHeight: 220, overflowY: 'auto' }}>
                          {contacts
                            .filter(c => { const n = `${c.first_name || ''} ${c.last_name || ''}`.toLowerCase(); const q = discussFilter.toLowerCase(); return n.includes(q) || (c.email || '').toLowerCase().includes(q); })
                            .slice(0, 30)
                            .map(c => (
                              <div key={c.id || c.email} onClick={() => startDiscussion(c)}
                                style={{ padding: '7px 8px', fontSize: 13, cursor: 'pointer', borderRadius: 6 }}
                                onMouseEnter={(e) => e.currentTarget.style.background = 'var(--caam-cream-deep)'}
                                onMouseLeave={(e) => e.currentTarget.style.background = '#fff'}>
                                {c.first_name} {c.last_name}
                              </div>
                            ))}
                          {contacts.length === 0 && <div style={{ padding: 10, fontSize: 12, color: 'var(--caam-text-light)' }}>No colleagues found</div>}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                <button style={styles.readActionBtn} onClick={handleArchive} title="Archive">
                  <IconArchive />
                </button>
                <button style={styles.readActionBtn} onClick={handleStar} title="Star">
                  <IconStarOutline />
                </button>
                <button style={styles.readActionBtn} onClick={handleDelete} title="Delete">
                  <IconTrash />
                </button>
                <button style={styles.readActionBtn} onClick={toggleUnread}
                  title={(selectedMsg.labelIds || []).includes('UNREAD') ? 'Mark as read' : 'Mark as unread'}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-10 5L2 7"/></svg>
                  <span style={{ marginLeft: 6 }}>{(selectedMsg.labelIds || []).includes('UNREAD') ? 'Mark read' : 'Mark unread'}</span>
                </button>
                <button
                  style={{ ...styles.readActionBtn, marginLeft: 'auto' }}
                  onClick={() => setSelectedId(null)}
                  title="Close"
                  aria-label="Close"
                >
                  <IconClose />
                </button>
              </div>
              <h2 style={styles.readSubject}>{selectedMsg.subject}</h2>
              <div style={styles.readMeta}>
                <div>
                  <strong>{parseFromName(selectedMsg.from)}</strong>{' '}
                  <span style={styles.muted}>&lt;{parseFromEmail(selectedMsg.from)}&gt;</span>
                </div>
                <div style={styles.muted}>to {selectedMsg.to}</div>
                {selectedMsg.cc && <div style={styles.muted}>cc {selectedMsg.cc}</div>}
                <div style={styles.muted}>{formatReadDate(selectedMsg.date)}</div>
              </div>
              {selectedMsg.attachments && selectedMsg.attachments.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 12 }}>
                  {selectedMsg.attachments.map((att, i) => (
                    <button key={i} type="button" onClick={() => downloadAttachment(att)} title={`Download ${att.filename}`}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'var(--caam-cream-deep)', border: '1px solid var(--caam-border)', borderRadius: 6, padding: '6px 10px', fontSize: 12, cursor: 'pointer', color: 'var(--caam-text)' }}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                      {att.filename} <span style={{ color: 'var(--caam-text-light)' }}>({Math.max(1, Math.ceil((att.size || 0) / 1024))} KB)</span>
                    </button>
                  ))}
                </div>
              )}
            </header>
            <iframe
              title="Email body"
              srcDoc={selectedMsg.body || '<p style="color:var(--caam-text-light)">(empty message)</p>'}
              sandbox="allow-popups allow-popups-to-escape-sandbox allow-same-origin"
              onLoad={(e) => { try { const d = e.target.contentDocument; if (d && d.body) e.target.style.height = Math.max(200, d.body.scrollHeight + 24) + 'px'; } catch (err) {} }}
              style={styles.readBodyFrame}
            />
            {thread.filter(m => m.id !== selectedMsg.id).length > 0 && (
              <div style={{ borderTop: '1px solid var(--caam-border)', margin: '0 32px', padding: '12px 0 24px' }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--caam-text-light)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8 }}>
                  Conversation · {thread.length} messages
                </div>
                {thread.filter(m => m.id !== selectedMsg.id).map(m => {
                  const open = expandedMsgs.has(m.id);
                  return (
                    <div key={m.id} style={{ border: '1px solid var(--caam-border)', borderRadius: 8, marginBottom: 8, overflow: 'hidden' }}>
                      <button onClick={() => setExpandedMsgs(prev => { const n = new Set(prev); n.has(m.id) ? n.delete(m.id) : n.add(m.id); return n; })}
                        style={{ display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center', gap: 8, padding: '10px 12px', background: 'var(--caam-cream-deep)', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--caam-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{parseFromName(m.from)}</span>
                        <span style={{ fontSize: 11, color: 'var(--caam-text-light)', flexShrink: 0 }}>{formatMsgDate(m.date)}</span>
                      </button>
                      {open && (
                        <iframe title={'msg-' + m.id}
                          srcDoc={m.body || '<p style="color:var(--caam-text-light)">(empty message)</p>'}
                          sandbox="allow-popups allow-popups-to-escape-sandbox allow-same-origin"
                          onLoad={(e) => { try { const d = e.target.contentDocument; if (d && d.body) e.target.style.height = Math.max(120, d.body.scrollHeight + 24) + 'px'; } catch (err) {} }}
                          style={{ width: '100%', height: 200, border: 'none', display: 'block', background: 'white' }} />
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}

// ===== Helpers =====

// Rough HTML -> plain text for loading a saved draft back into the compose textarea.
function htmlToText(html) {
  return (html || '')
    .replace(/<\s*br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div)>/gi, '\n\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&#39;/g, "'").replace(/&quot;/g, '"')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

// Escape HTML special chars when inserting user-derived text into iframe-rendered body
function escapeHtml(s) {
  if (s == null) return '';
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

// Pull email out of "Name <email>" — empty string if no angle brackets found.
function parseFromEmail(rawFrom) {
  if (!rawFrom) return '';
  const m = rawFrom.match(/<([^>]+)>/);
  return m ? m[1].trim() : rawFrom.trim();
}

// Full timestamp for the reading pane: "Wed, May 13, 2026, 3:03 PM"
function formatReadDate(rawDate) {
  if (!rawDate) return '';
  const d = new Date(rawDate);
  if (isNaN(d.getTime())) return rawDate;
  return d.toLocaleString([], {
    weekday: 'short', year: 'numeric', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit'
  });
}

// "Jon Lamirault <jlamirault@caamuseum.org>" -> "Jon Lamirault"
// "jlamirault@caamuseum.org" -> "jlamirault@caamuseum.org"
function parseFromName(rawFrom) {
  if (!rawFrom) return '(unknown sender)';
  const m = rawFrom.match(/^\s*"?([^"<]+?)"?\s*<[^>]+>\s*$/);
  return m ? m[1].trim() : rawFrom.trim();
}

// Today: "10:42 AM"  /  this week: "Mon"  /  older: "May 9"  /  older year: "May 9, 2025"
function formatMsgDate(rawDate) {
  if (!rawDate) return '';
  const d = new Date(rawDate);
  if (isNaN(d.getTime())) return '';
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) {
    return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  }
  const sameYear = d.getFullYear() === now.getFullYear();
  const dayDiff = (now - d) / 86400000;
  if (dayDiff < 7) {
    return d.toLocaleDateString([], { weekday: 'short' });
  }
  if (sameYear) {
    return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  }
  return d.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
}

// ===== Styles (inline, Apple-aesthetic per Bible §12.4) =====

const styles = {
  centered: {
    height: '100%',
    minHeight: 400,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40,
    color: 'var(--caam-text)'
  },
  muted: { color: 'var(--caam-text-light)', fontSize: 13 },
  primaryBtn: {
    padding: '10px 20px',
    fontSize: 14,
    fontWeight: 500,
    background: 'var(--caam-accent)',
    color: 'white',
    border: 'none',
    borderRadius: 6,
    cursor: 'pointer'
  },
  shell: {
    display: 'flex',
    // Fill the content area's height exactly so the 3 panes scroll internally and the
    // outer page never scrolls (no negative margins — those clipped the top when the
    // content area had a retained scroll position from another view). Flat, not boxed.
    height: '100%',
    background: 'var(--caam-cream-deep)',
    overflow: 'hidden'
  },
  folderPane: {
    width: 200,
    flexShrink: 0,
    padding: '16px 8px',
    borderRight: '1px solid var(--caam-border)',
    background: 'var(--caam-cream-deep)',
    display: 'flex',
    flexDirection: 'column'
  },
  composeBtn: {
    margin: '0 8px 16px 8px',
    padding: '10px 16px',
    fontSize: 14,
    fontWeight: 500,
    background: 'var(--caam-accent)',
    color: 'white',
    border: 'none',
    borderRadius: 6,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'flex-start'
  },
  folderList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2
  },
  folderItem: {
    display: 'flex',
    alignItems: 'center',
    padding: '8px 12px',
    fontSize: 13,
    color: 'var(--caam-text-muted)',
    background: 'transparent',
    border: 'none',
    borderRadius: 6,
    textAlign: 'left',
    cursor: 'pointer',
    width: '100%'
  },
  folderItemActive: {
    background: 'var(--caam-accent-tint)',
    color: 'var(--caam-accent)',
    fontWeight: 500
  },
  labelsHeading: {
    fontSize: 11,
    fontWeight: 500,
    color: 'var(--caam-text-light)',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    padding: '16px 12px 8px'
  },
  labelDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    marginRight: 10,
    flexShrink: 0,
    display: 'inline-block'
  },
  labelName: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  listPane: {
    width: 380,
    flexShrink: 0,
    borderRight: '1px solid var(--caam-border)',
    background: 'white',
    display: 'flex',
    flexDirection: 'column'
  },
  searchBar: {
    padding: '10px 14px',
    borderBottom: '1px solid var(--caam-border)',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    position: 'relative'
  },
  searchIcon: {
    color: 'var(--caam-text-light)',
    display: 'inline-flex',
    alignItems: 'center'
  },
  searchInput: {
    flex: 1,
    height: 32,
    padding: '0 8px',
    fontSize: 13,
    border: 'none',
    background: 'transparent',
    outline: 'none',
    boxSizing: 'border-box'
  },
  searchClear: {
    background: 'transparent',
    border: 'none',
    color: 'var(--caam-text-light)',
    fontSize: 18,
    cursor: 'pointer',
    padding: '0 4px',
    lineHeight: 1
  },
  msgRow: {
    display: 'block',
    width: '100%',
    textAlign: 'left',
    // Every row reserves the 3px left rail (transparent here, gold on unread) so read and
    // unread rows share identical geometry and content lines up — no read-row drift.
    padding: '10px 16px 10px 13px',
    background: 'transparent',
    borderTop: 'none',
    borderRight: 'none',
    borderBottom: '1px solid var(--caam-cream-deep)',
    borderLeft: '3px solid transparent',
    boxSizing: 'border-box',
    cursor: 'pointer',
    color: 'var(--caam-text)'
  },
  msgRowSelected: {
    background: 'var(--caam-accent-tint)'
  },
  msgRowUnread: {
    fontWeight: 700,
    borderLeftColor: 'var(--caam-accent)'  // colors the always-reserved 3px left rail
  },
  msgRowTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    marginBottom: 2
  },
  msgRowFrom: {
    fontSize: 13,
    color: 'var(--caam-text)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: 240
  },
  msgRowDate: {
    fontSize: 11,
    color: 'var(--caam-text-light)',
    flexShrink: 0,
    marginLeft: 8
  },
  msgRowSubject: {
    fontSize: 13,
    color: 'var(--caam-text)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    marginBottom: 2
  },
  msgRowSnippet: {
    fontSize: 12,
    color: 'var(--caam-text-light)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  readHeader: {
    padding: '16px 32px',
    borderBottom: '1px solid var(--caam-border)',
    background: 'white'
  },
  readActionsRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    marginBottom: 12
  },
  readActionBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '6px 10px',
    fontSize: 13,
    color: 'var(--caam-text-muted)',
    background: 'transparent',
    border: '1px solid transparent',
    borderRadius: 6,
    cursor: 'pointer'
  },
  bulkBtn: {
    padding: '4px 10px',
    fontSize: 12,
    border: '1px solid var(--caam-border)',
    borderRadius: 6,
    background: '#fff',
    color: 'var(--caam-text-muted)',
    cursor: 'pointer'
  },
  readSubject: {
    margin: '0 0 8px 0',
    fontSize: 18,
    fontWeight: 500,
    color: 'var(--caam-text)'
  },
  readMeta: {
    fontSize: 12,
    color: 'var(--caam-text-muted)',
    lineHeight: 1.6
  },
  readBodyFrame: {
    width: 'calc(100% - 64px)',
    margin: '0 32px 24px',
    height: 400,        // initial; auto-sized to content on load (see iframe onLoad)
    minHeight: 200,
    display: 'block',
    border: 'none',
    background: 'white'
  },
  modalOverlay: {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.4)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  },
  modalCard: {
    width: 600,
    maxWidth: '90vw',
    maxHeight: '85vh',
    background: 'white',
    borderRadius: 8,
    boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
    display: 'flex',
    flexDirection: 'column'
  },
  modalHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 18px',
    borderBottom: '1px solid var(--caam-border)'
  },
  modalTitle: {
    fontSize: 15,
    fontWeight: 500,
    color: 'var(--caam-text)'
  },
  modalClose: {
    background: 'transparent',
    border: 'none',
    color: 'var(--caam-text-muted)',
    cursor: 'pointer',
    padding: 4,
    display: 'inline-flex',
    alignItems: 'center'
  },
  modalForm: {
    display: 'flex',
    flexDirection: 'column',
    padding: 18,
    gap: 10,
    overflow: 'auto'
  },
  modalLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    borderBottom: '1px solid var(--caam-border)',
    paddingBottom: 8
  },
  modalLabelText: {
    fontSize: 12,
    color: 'var(--caam-text-muted)',
    width: 56,
    flexShrink: 0
  },
  modalInput: {
    flex: 1,
    border: 'none',
    outline: 'none',
    fontSize: 14,
    padding: '6px 0',
    color: 'var(--caam-text)',
    background: 'transparent'
  },
  modalTextarea: {
    width: '100%',
    minHeight: 220,
    maxHeight: 360,
    border: '1px solid var(--caam-border)',
    borderRadius: 6,
    padding: 10,
    fontSize: 13,
    fontFamily: 'inherit',
    resize: 'vertical',
    outline: 'none',
    color: 'var(--caam-text)',
    boxSizing: 'border-box'
  },
  quotedToggleWrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    marginTop: -2
  },
  quotedToggle: {
    alignSelf: 'flex-start',
    background: 'var(--caam-cream-deep)',
    color: 'var(--caam-text-muted)',
    border: 'none',
    borderRadius: 12,
    padding: '4px 12px',
    fontSize: 12,
    cursor: 'pointer'
  },
  quotedPreview: {
    fontSize: 12,
    color: 'var(--caam-text-muted)',
    border: '1px solid var(--caam-border)',
    borderRadius: 6,
    padding: 10,
    maxHeight: 200,
    overflow: 'auto',
    background: 'var(--caam-cream-deep)'
  },
  modalError: {
    color: '#9B3A1F',
    fontSize: 13,
    padding: '4px 0'
  },
  modalSuccess: {
    color: '#0a7c2f',
    fontSize: 13,
    padding: '4px 0'
  },
  modalFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 8,
    paddingTop: 6
  },
  modalCancel: {
    padding: '8px 16px',
    fontSize: 13,
    color: 'var(--caam-text-muted)',
    background: 'transparent',
    border: '1px solid var(--caam-border)',
    borderRadius: 6,
    cursor: 'pointer'
  },
  modalSend: {
    padding: '8px 18px',
    fontSize: 13,
    fontWeight: 500,
    color: 'white',
    background: 'var(--caam-accent)',
    border: 'none',
    borderRadius: 6,
    cursor: 'pointer'
  },
  listScroller: {
    flex: 1,
    overflowY: 'auto',
    // Gutter so message rows never butt up against the folder pane on the left
    // (read rows have no gold rail, so without this they look like they run into it).
    padding: '0 8px'
  },
  readPane: {
    flex: 1,
    background: 'white',
    overflowY: 'auto'
  }
};
